import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';
import { isLocalLibraryAlbumKey } from '../services/localLibraryMetadata';
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';

/**
 * useAlbumCredits — Aggregates album credits from multiple sources.
 *
 * Sources (in priority order):
 *   1. API album data (/api/albums/:id) → mirrorCredits, credits, production_credits
 *   2. MusicBrainz mirror → /api/mirror/releases/:mbid/credits
 *   3. Qobuz performers string (album-level + per-track)
 *   4. Qobuz album-detail raw response (performers field)
 *
 * Returns:
 *   - creditsByRole: { [roleName]: [{ name, mbid?, instruments? }] } | null
 *   - loading: boolean
 *   - error: string | null
 *   - refetch: () => void  — manually re-fetch credits
 *   - sources: string[]    — which sources contributed data (for debugging)
 */

const ROLE_ALIASES = {
  'MainArtist': 'Primary Artist',
  'Main Artist': 'Primary Artist',
  'AssociatedPerformer': 'Performer',
  'Associated Performer': 'Performer',
  'ComposerLyricist': 'Songwriter',
  'Composer Lyricist': 'Songwriter',
  'FeaturedArtist': 'Featured',
  'Featured Artist': 'Featured',
  'StudioPersonnel': 'Studio',
  'Studio Personnel': 'Studio',
};

function normalizeRole(raw) {
  if (!raw) return '';
  let role = String(raw).replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return ROLE_ALIASES[role] || role;
}

function dedupeEntries(entries) {
  const seen = new Set();
  return entries.filter(e => {
    const key = (e.name || '').toLowerCase().trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function mergeRoleMaps(...maps) {
  const merged = {};
  for (const map of maps) {
    if (!map || typeof map !== 'object') continue;
    for (const [role, entries] of Object.entries(map)) {
      const normRole = normalizeRole(role);
      if (!normRole) continue;
      if (!merged[normRole]) merged[normRole] = [];
      const arr = Array.isArray(entries) ? entries : [];
      for (const e of arr) {
        const name = e?.artist_name || e?.name || (typeof e === 'string' ? e : null);
        if (!name) continue;
        merged[normRole].push({
          name,
          mbid: e?.artist_mbid || e?.mbid || null,
          instruments: e?.instruments || e?.attributes || null,
        });
      }
    }
  }
  for (const role of Object.keys(merged)) {
    merged[role] = dedupeEntries(merged[role]);
    if (merged[role].length === 0) delete merged[role];
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

function parseQobuzPerformers(performersStr, trackCreditsArr) {
  if (!performersStr && !trackCreditsArr?.length) return null;
  const roleMap = {};

  const addEntry = (name, role) => {
    const normRole = normalizeRole(role);
    if (!normRole || !name) return;
    if (!roleMap[normRole]) roleMap[normRole] = [];
    if (!roleMap[normRole].some(e => e.name.toLowerCase() === name.toLowerCase())) {
      roleMap[normRole].push({ name, mbid: null });
    }
  };

  if (performersStr && typeof performersStr === 'string') {
    const entries = performersStr.split(' - ').map(s => s.trim()).filter(Boolean);
    for (const entry of entries) {
      const parts = entry.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      const name = parts[0];
      for (let i = 1; i < parts.length; i++) {
        addEntry(name, parts[i]);
      }
    }
  }

  if (Array.isArray(trackCreditsArr)) {
    for (const tc of trackCreditsArr) {
      if (tc.composer?.name) addEntry(tc.composer.name, 'Composer');
      if (tc.performers && typeof tc.performers === 'string') {
        const tEntries = tc.performers.split(' - ').map(s => s.trim()).filter(Boolean);
        for (const te of tEntries) {
          const tParts = te.split(',').map(s => s.trim()).filter(Boolean);
          if (tParts.length < 2) continue;
          const name = tParts[0];
          for (let i = 1; i < tParts.length; i++) {
            addEntry(name, tParts[i]);
          }
        }
      }
    }
  }

  return Object.keys(roleMap).length > 0 ? roleMap : null;
}

const PREFERRED_ROLE_ORDER = [
  'Primary Artist', 'Featured', 'Performer', 'Vocals', 'Guitar',
  'Bass', 'Drums', 'Keyboard', 'Piano', 'Composer', 'Songwriter',
  'Lyricist', 'Producer', 'Engineer', 'Mixing', 'Mastering', 'Studio',
];

function sortRoles(creditsByRole) {
  if (!creditsByRole) return null;
  const entries = Object.entries(creditsByRole);
  entries.sort((a, b) => {
    const ai = PREFERRED_ROLE_ORDER.findIndex(r => a[0].toLowerCase().includes(r.toLowerCase()));
    const bi = PREFERRED_ROLE_ORDER.findIndex(r => b[0].toLowerCase().includes(r.toLowerCase()));
    const aIdx = ai >= 0 ? ai : 999;
    const bIdx = bi >= 0 ? bi : 999;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return a[0].localeCompare(b[0]);
  });
  return Object.fromEntries(entries);
}

export default function useAlbumCredits({
  albumId,
  albumTitle,
  artistName,
  qobuzAlbumId,
  qobuzFullAlbum,
  releaseGroupMbid,
  apiAlbum,
  album,
}) {
  const [apiCredits, setApiCredits] = useState(null);
  const [mirrorCredits, setMirrorCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const fetchRef = useRef(0);

  const fetchCredits = useCallback(async () => {
    const fetchId = ++fetchRef.current;
    setLoading(true);
    setError(null);
    const activeSources = [];

    try {
      const promises = [];

      // Source 1: Fetch from /api/albums/:id for mirror credits + production credits
      const skipCloudAlbumFetch = shouldUseLocalLibraryIpc() && isLocalLibraryAlbumKey(albumId);
      if (albumId && !skipCloudAlbumFetch) {
        promises.push(
          fetch(`${CLOUD_API_URL}/api/albums/${encodeURIComponent(albumId)}?_cb=${Date.now()}`, { cache: 'no-store' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (fetchId !== fetchRef.current) return;
              if (data) {
                const creds = data.mirrorCredits || data.mirror_credits || data.credits || null;
                if (creds && typeof creds === 'object' && Object.keys(creds).length > 0) {
                  setApiCredits(creds);
                  activeSources.push('api');
                }
              }
            })
            .catch(() => null)
        );
      }

      // Source 2: MusicBrainz mirror by MBID
      if (releaseGroupMbid) {
        promises.push(
          fetch(`${CLOUD_API_URL}/api/mirror/releases/${encodeURIComponent(releaseGroupMbid)}/credits`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (fetchId !== fetchRef.current) return;
              const cr = data?.credits?.credits_by_role || data?.credits?.creditsByRole || data?.credits_by_role || null;
              if (cr && Object.keys(cr).length > 0) {
                setMirrorCredits(cr);
                activeSources.push('mirror');
              }
            })
            .catch(() => null)
        );
      }

      // Source 3: Search MusicBrainz mirror by title+artist when no UUID
      if (!releaseGroupMbid && albumTitle) {
        promises.push(
          fetch(`${CLOUD_API_URL}/api/mirror/search/release-groups?query=${encodeURIComponent(albumTitle + (artistName ? ' ' + artistName : ''))}&limit=3`)
            .then(r => r.ok ? r.json() : null)
            .then(async (data) => {
              if (fetchId !== fetchRef.current) return;
              const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
              const match = results.find(r => {
                const t = String(r?.title || '').toLowerCase();
                return t === albumTitle.toLowerCase() || t.includes(albumTitle.toLowerCase());
              }) || results[0];
              const mbid = match?.id || match?.mbid || null;
              if (mbid) {
                try {
                  const cr = await fetch(`${CLOUD_API_URL}/api/mirror/releases/${encodeURIComponent(mbid)}/credits`).then(r => r.ok ? r.json() : null);
                  if (fetchId !== fetchRef.current) return;
                  const byRole = cr?.credits?.credits_by_role || cr?.credits?.creditsByRole || cr?.credits_by_role || null;
                  if (byRole && Object.keys(byRole).length > 0) {
                    setMirrorCredits(byRole);
                    activeSources.push('mirror-search');
                  }
                } catch { /* ignore */ }
              }
            })
            .catch(() => null)
        );
      }

      // Source 4: Qobuz album-detail (raw performers) — fetch if not already available
      if (qobuzAlbumId && !qobuzFullAlbum?.performers && !qobuzFullAlbum?.trackCredits) {
        promises.push(
          fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album-detail?album_id=${qobuzAlbumId}`, { headers: getHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (fetchId !== fetchRef.current) return;
              const detail = data?.album || data;
              if (detail?.performers) {
                activeSources.push('qobuz-detail');
              }
            })
            .catch(() => null)
        );
      }

      await Promise.allSettled(promises);
      if (fetchId === fetchRef.current) {
        setSources(activeSources);
      }
    } catch (e) {
      if (fetchId === fetchRef.current) {
        setError(e?.message || 'Failed to load credits');
      }
    } finally {
      if (fetchId === fetchRef.current) {
        setLoading(false);
      }
    }
  }, [albumId, albumTitle, artistName, qobuzAlbumId, releaseGroupMbid]);

  useEffect(() => {
    setApiCredits(null);
    setMirrorCredits(null);
    setSources([]);
    fetchCredits();
  }, [albumId, qobuzAlbumId, releaseGroupMbid]);

  const creditsByRole = useMemo(() => {
    // Priority: album prop mirror credits > apiAlbum credits > fetched api credits > mirror credits > Qobuz performers
    const albumMirror = album?.mirrorCredits || album?.mirror_credits;
    const albumCredits = album?.credits;
    const apiAlbumMirror = apiAlbum?.mirrorCredits || apiAlbum?.mirror_credits;
    const apiAlbumCredits = apiAlbum?.credits;

    const qobuzParsed = parseQobuzPerformers(
      qobuzFullAlbum?.performers,
      qobuzFullAlbum?.trackCredits
    );

    const merged = mergeRoleMaps(
      albumMirror,
      apiAlbumMirror,
      apiCredits,
      mirrorCredits,
      albumCredits,
      apiAlbumCredits,
      qobuzParsed,
    );

    return sortRoles(merged);
  }, [album?.mirrorCredits, album?.mirror_credits, album?.credits, apiAlbum, apiCredits, mirrorCredits, qobuzFullAlbum]);

  return {
    creditsByRole,
    loading,
    error,
    refetch: fetchCredits,
    sources,
  };
}
